import { React } from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleQuestion, faPerson, faMagnifyingGlass, faPersonWalking, faChartLine } from '@fortawesome/free-solid-svg-icons';
import { useCollapse } from 'react-collapsed';
import { Carousel } from 'react-responsive-carousel';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import './FeaturesPage.css';

function FeaturesPage() {
    // Animation for motion divs
    const fadeInUp = {
        initial: { opacity: 0, y: 20 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.6 }
    };

    const collapse1 = useCollapse();
    const collapse2 = useCollapse();
    const collapse3 = useCollapse();
    const collapse4 = useCollapse();

    return (
        <div className="features-page">
            {/* Floating Gradient Orbs */}
            <div className="floating-orbs">
                <div className="orb orb-1"></div>
                <div className="orb orb-2"></div>
                <div className="orb orb-3"></div>
                <div className="orb orb-4"></div>
                <div className="orb orb-5"></div>
            </div>
            
            <div className="features-container">
                <motion.div className="page-hero-section"
                initial={{ opacity: 0, y: -30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}>
                    <h1 className="page-hero-title">Multiple Clinic Features for <span>Easy Convenience</span></h1>
                    <p className="page-hero-subtitle">Our Clinic Account provides clinicians, caregivers, and other users with multiple features to monitor and document all their player-patient's game sessions.</p>
                </motion.div>

                { /* Clinic Features Table of Contents */}
                <motion.section className="features-table-of-contents"
                {...fadeInUp}
                transition={{ delay: 0.2 }}>
                    <div className="section-header">
                        <div className="section-icon"><FontAwesomeIcon icon={faCircleQuestion} color='#1515b6ff'/></div>
                        <h2 className="section-title">How is it used?</h2>
                    </div>
                    <p>Clinicians can monitor patient game play performance in real time either in person (on site clinic sessions) or remotely (home exercise sessions) and adjust game parameters to change game difficulty during or between sessions. This remote option gives a clinician the ability to monitor how often the patient participated in a home exercise program and how successful the experience was towards achieving therapy goals. From the clinician website, the clinician can adjust the home game parameters to meet the patient needs and move towards achieving therapy goals.</p>
                    <h2>Features Include:</h2>
                    <p><a href="#view-player-sessions">View All Patient Sessions</a> - An overview of their entire group of patients where the therapist can track progress over time</p>
                    <p><a href="#view-session-details">View Session Details</a> - Details of a single play session where the therapist can see patient game performance metrics: scores, duration, and performance through graphs and tables</p>
                    <p><a href="#3d-playback">3D Playback</a> - An avatar replay of the patient’s movements from a play session for remote evaluation of quality of movement</p>
                    <p><a href="#game-parameters">Changing/Adjusting Game Parameters</a> - The ability to override game parameters remotely to adjust in-home game sessions</p>
                </motion.section>
                <motion.section id="view-player-sessions" className="feature-card"
                {...fadeInUp}
                transition={{ delay: 0.3 }}>
                    <div className="feature-text-content">
                        <div className="feature-header-card">
                            <div className="section-header">
                                <div className="section-icon"><FontAwesomeIcon icon={faPerson} color='#000d96ff'/></div>
                                <h2>View All Patient/Player Sessions</h2>
                            </div>
                            <p>Clinicians can track patient performance to examine changes over time. Clinicians can 
                            choose from a menu of data representation options to document patient performance and monitor patient progress towards achieving therapy goals.</p>
                        </div>
                        <div className="feature-collapsible" {...collapse1.getToggleProps()}>
                        {collapse1.isExpanded ? '-' : '+'}
                        </div>
                        <div {...collapse1.getCollapseProps()}>
                            <div className="feature-list-card">
                                <ul>
                                    <li>Select, filter, and sort by patient, by a date or a range of dates, and/or by game</li>
                                    <li>View patient minimum or maximum (min/max) ROM for multiple joints for a game play session (including uniaxial, biaxial and tri-axial joints)</li>
                                    <li>Select any game session for detailed information on that session, as described in the View Session Details section below</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                    
                    <Carousel
                        className="carousel1"
                        showThumbs={false}
                        transitionTime={1500}
                        swipeable
                        emulateTouch
                        swipeScrollTolerance={120}
                    >
                        <div><img src="/images/clinic-features/player-session1.png" alt="Patients Information" /></div>
                        <div><img src="/images/clinic-features/player-session2.png" alt="Patient Session" /></div>
                    </Carousel>
                </motion.section>
                <motion.section id="view-session-details" className="feature-card"
                {...fadeInUp}
                transition={{ delay: 0.4 }}>
                    <div className="feature-text-content">
                        <div className="feature-header-card">
                            <div className="section-header">
                                <div className="section-icon"><FontAwesomeIcon icon={faMagnifyingGlass} color='#000d96ff'/></div>
                                <h2>View Session Details</h2>
                            </div>
                            <p>Each session includes detailed individual performance data. Clinicians can examine patient movement from any game session to determine if the patient is meeting therapy goals and if game parameters need to be revised to optimize movement. Clinicians may view details of any session, including:</p>
                        </div>
                        <div className="feature-collapsible" {...collapse2.getToggleProps()}>
                        {collapse2.isExpanded ? '-' : '+'}
                        </div>
                        <div {...collapse2.getCollapseProps()}>
                            <div className="feature-list-card">
                                <ul>
                                    <li>Patient gaming performance – game score and game duration (timing)</li>
                                    <li>Patient joint ROM during a game session (from a table of 23 body marker min/max positions)</li>
                                    <li>Position graphs of 3D (X,Y,Z) body marker data for 23 body markers over an entire session, and min-max summary tables</li>
                                    <li>Graphs of joint angles and velocities over an entire session (including uniaxial, biaxial and tri-axial joints) for over one dozen joints, and min-max ROM summary tables</li>
                                    <li>Interactive graphs allowing you to zoom in on session data to see sub-second motion</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                    <Carousel
                        className="carousel2"
                        showThumbs={false}
                        transitionTime={2500}
                        swipeable
                        emulateTouch
                        swipeScrollTolerance={120}
                    >
                        <div>
                            <img src="/images/clinic-features/session-details1.png" alt="Patients Information" />
                        </div>
                        <div>
                            <img src="/images/clinic-features/session-details2.png" alt="Patient Session" />
                        </div>
                        <div>
                            <img src="/images/clinic-features/session-details3.png" alt="Patient Session" />
                        </div>
                        <div>
                            <img src="/images/clinic-features/session-details4.png" alt="Patient Session" />
                        </div>
                        <div>
                            <img src="/images/clinic-features/session-details5.png" alt="Patient Session" />
                        </div>
                    </Carousel>
                </motion.section>
                <motion.section id="3d-playback" className="feature-card"
                {...fadeInUp}
                transition={{ delay: 0.5 }}>
                    <div className="feature-text-content">
                        <div className="feature-header-card">
                            <div className="section-header">
                                <div className="section-icon"><FontAwesomeIcon icon={faPersonWalking} color='#000d96ff'/></div>
                                <h2>3D Playback</h2>
                            </div>
                            <p>Clinicians can view patient sessions using the 3D Playback feature using an avatar. This feature allows the clinician to see the "quality, extent, and amount of movement" during the gaming session. This information is not available from charts and graphs and gives the clinician the ability to view patient movement during game sessions to determine if and how they should adjust game parameters for future sessions.</p>
                        </div>
                        <div className="feature-collapsible" {...collapse3.getToggleProps()}>
                        {collapse3.isExpanded ? '-' : '+'}
                        </div>
                        <div {...collapse3.getCollapseProps()}>
                            <div className="feature-list-card">
                                <ul>
                                    <li>No HIPAA violations because patient images are not saved in the game data recording</li>
                                    <li>Ability to view sessions from multiple angles with full camera control including zoom, pan, and rotate</li>
                                    <li>DVR-like controls including rewind, playback speed with fast forward (up to 10X) and slow motion, pause, and reverse features</li>
                                    <li>Tracing body parts to generate movement/reach volumes for visualizing movement without monitoring entire session</li>
                                    <li>Virtual goniometers/protractors show real time joint ROM during the 3-D playback session to identify joint movement during the session</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                    <Carousel
                        className="carousel3"
                        showThumbs={false}
                        transitionTime={2500}
                        swipeable
                        emulateTouch
                        swipeScrollTolerance={120}
                    >
                        <div>
                            <img src="/images/clinic-features/3d-playback1.jpg" alt="Patients Information" />
                        </div>
                        <div>
                            <img src="/images/clinic-features/3d-playback2.jpg" alt="Patient Session" />
                        </div>
                    </Carousel>
                </motion.section>
                <motion.section id="game-parameters" className="feature-card"
                {...fadeInUp}
                transition={{ delay: 0.6 }}>
                    <div className="feature-text-content">
                        <div className="feature-header-card">
                            <div className="section-header">
                                <div className="section-icon"><FontAwesomeIcon icon={faChartLine} color='#000d96ff'/></div>
                                <h2>Adjusting Game Parameters</h2>
                            </div>
                            <p>Clinicians can adjust multiple gaming parameters for each of the games available in the EAG suite of games after reviewing patient performance. Adjusting game parameters will make the games more accessible to patients, and clinicians can increase or decrease game difficulty to achieve the patient's therapy goals. All game parameters for all of the video games in the enAbleGames site can be adjusted either on-site for clinic game sessions or remotely for home exercise programs by using our simple web interface.</p>
                        </div>
                        <div className="feature-collapsible" {...collapse4.getToggleProps()}>
                        {collapse4.isExpanded ? '-' : '+'}
                        </div>
                        <div {...collapse4.getCollapseProps()}>
                            <div className="feature-list-card">
                                <ul>
                                    <li>Body tracking & movement that will "engage" the game</li>
                                    <ul>
                                        <li>Allows a game to be played by motion from unilateral or bilateral upper extremity (UE) or lower extremity (LE) movement or by trunk lateral flexion or weight shifting</li>
                                        <li>Enables the clinician to choose game play to focus on desired patient movement</li>
                                    </ul>
                                    <li>Number of objects in the game that are "positive" (ones to collect/hit) or "negative" (ones to avoid) during game play to change degree of difficulty and scoring</li>
                                    <li>Length of a game play session (duration)</li>
                                    <li>Speed of a game play session (intensity)</li>
                                    <li>Graphic design (theme) of a game play session (engagement)</li>
                                    <li>A variety of special effects that can be used or disabled depending on patients sensory needs. These special effects may:</li>
                                    <ul>
                                        <li>Promote attention/memory by adjusting features</li>
                                        <li>Help motivate players and provide feedback (voice coach feature)</li>
                                        <li>Increase motivation by adding "paint splats" to create colorful visuals</li>
                                        <li>Increase enjoyment and motivation with music</li>
                                    </ul>
                                </ul>
                            </div>
                        </div>
                    </div>
                    <img src="/images/clinic-features/parameters1.png" alt="Patients Information" className="parameters-img" />
                </motion.section>
            </div>
        </div>
    );
}

export default FeaturesPage;